import { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowRight, ChevronLeft, ChevronRight,
  Coffee, Backpack, BookOpen, GraduationCap, ShoppingBag,
  Trophy, Award, Target, Flame, Flag,
  Gift, Snowflake, Star, Sparkles, TreePalm,
  Sun, Waves, Music, Zap, Tag, Ticket, Pizza, CupSoda,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── CSS Keyframes ───────────────────────────────────────────────────────────
const KEYFRAMES = `
  @keyframes sp-kenburns {
    0%   { transform: scale(1.0) translate(0,0); }
    100% { transform: scale(1.08) translate(-1%,0.5%); }
  }
  @keyframes sp-float-a {
    0%,100% { transform: translateY(0px) rotate(-2deg); }
    40%     { transform: translateY(-14px) rotate(2deg); }
    70%     { transform: translateY(-7px) rotate(-1deg); }
  }
  @keyframes sp-float-b {
    0%,100% { transform: translateY(0px) rotate(1.5deg); }
    35%     { transform: translateY(-10px) rotate(-2.5deg); }
    65%     { transform: translateY(-18px) rotate(1deg); }
  }
  @keyframes sp-float-c {
    0%,100% { transform: translateY(0px) rotate(-1deg); }
    50%     { transform: translateY(-12px) rotate(2.5deg); }
  }
  @keyframes sp-lightsweep {
    0%   { transform: translateX(-150%) skewX(-12deg); opacity:0; }
    4%   { opacity:1; }
    38%  { opacity:0.7; }
    50%  { transform: translateX(160%) skewX(-12deg); opacity:0; }
    100% { transform: translateX(160%) skewX(-12deg); opacity:0; }
  }
  @keyframes sp-pulse-dot {
    0%,100% { opacity:1; box-shadow: 0 0 0 0 rgba(200,169,126,0.7); }
    50%     { opacity:0.65; box-shadow: 0 0 0 5px rgba(200,169,126,0); }
  }
  @keyframes sp-bounce-arrow {
    0%,100% { transform: translateY(0); opacity:0.8; }
    50%     { transform: translateY(7px); opacity:0.4; }
  }
  @keyframes sp-stat-cycle {
    0%   { opacity:0; transform:translateY(10px); }
    10%  { opacity:1; transform:translateY(0); }
    82%  { opacity:1; transform:translateY(0); }
    100% { opacity:0; transform:translateY(-10px); }
  }
  @keyframes sp-bubble-float {
    0%,100% { transform:translateY(0) scale(1); }
    50%     { transform:translateY(-9px) scale(1.03); }
  }
  @keyframes sp-accent-breathe {
    0%,100% { width:34px; opacity:1; }
    50%     { width:58px; opacity:0.85; }
  }
  @keyframes sp-card-enter {
    0%   { opacity:0; transform:translateX(-20px); }
    100% { opacity:1; transform:translateX(0); }
  }
  @keyframes sp-shimmer {
    0%   { background-position:200% center; }
    100% { background-position:-200% center; }
  }
`;

let _keyframesInjected = false;
function injectKeyframes() {
  if (_keyframesInjected || document.getElementById('sp-v2-kf')) return;
  _keyframesInjected = true;
  const el = document.createElement('style');
  el.id = 'sp-v2-kf';
  el.textContent = KEYFRAMES;
  document.head.appendChild(el);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function trackEvent(bannerId, type) {
  if (!bannerId) return;
  const device = window.innerWidth < 768 ? 'mobile' : 'desktop';
  fetch(`${API}/spotlight/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ banner_id: bannerId, event_type: type, device }),
  }).catch(() => {});
}

function detectTheme(banner) {
  const txt = `${banner?.title || ''} ${banner?.subtitle || ''}`.toLowerCase();
  if (/game\s*day|football|tailgate|touchdown|stadium|kickoff/i.test(txt)) return 'gameday';
  if (/holiday|christmas|winter|snow|festive|new\s*year/i.test(txt)) return 'holiday';
  if (/summer|beach|spring|tropical/i.test(txt)) return 'summer';
  if (/welcome|back.*school|semester|fall.*deals|move.?in|orientation/i.test(txt)) return 'welcome';
  return 'default';
}

// ─── Theme floating icon sets (Lucide icons, NOT emoji) ──────────────────────
const THEME_ICONS = {
  welcome: [
    { Icon: Coffee,        bg: ['#5C3D2E','#8B6347'], delay: 0,    dur: 5.5 },
    { Icon: Backpack,      bg: ['#1A3F4A','#3A7A8B'], delay: 1.8,  dur: 6.2 },
    { Icon: BookOpen,      bg: ['#2A4228','#4A7A52'], delay: 3.2,  dur: 5.0 },
    { Icon: GraduationCap, bg: ['#3C2D6B','#5A4A8A'], delay: 0.9,  dur: 6.8 },
    { Icon: Pizza,         bg: ['#5C2C14','#A05030'], delay: 2.5,  dur: 5.3 },
    { Icon: ShoppingBag,   bg: ['#2A3D50','#4A6A8B'], delay: 4.1,  dur: 6.0 },
  ],
  gameday: [
    { Icon: Trophy,  bg: ['#4A3010','#8A6030'], delay: 0,    dur: 6.0 },
    { Icon: Award,   bg: ['#4A2828','#8A4444'], delay: 1.6,  dur: 5.5 },
    { Icon: Target,  bg: ['#3A1818','#7A3030'], delay: 3.2,  dur: 6.5 },
    { Icon: Flame,   bg: ['#5C2A14','#A04820'], delay: 0.8,  dur: 5.2 },
    { Icon: Star,    bg: ['#4A3A10','#8A7030'], delay: 2.4,  dur: 5.8 },
    { Icon: CupSoda, bg: ['#1A3050','#3A6090'], delay: 4.0,  dur: 6.3 },
  ],
  holiday: [
    { Icon: Gift,      bg: ['#5A1818','#A03030'], delay: 0,    dur: 5.8 },
    { Icon: Snowflake, bg: ['#1A3855','#3A6A9A'], delay: 1.5,  dur: 6.5 },
    { Icon: Sparkles,  bg: ['#4A3A10','#8A7020'], delay: 3.0,  dur: 5.0 },
    { Icon: Star,      bg: ['#5A4A10','#A08020'], delay: 0.7,  dur: 6.2 },
    { Icon: Coffee,    bg: ['#5C3D2E','#8B6347'], delay: 2.1,  dur: 5.6 },
    { Icon: TreePalm,  bg: ['#1A3F28','#3A6A48'], delay: 3.8,  dur: 6.0 },
  ],
  summer: [
    { Icon: Sun,     bg: ['#5C4010','#A07020'], delay: 0,    dur: 5.5 },
    { Icon: Waves,   bg: ['#1A3A5A','#3A6A9A'], delay: 1.6,  dur: 6.0 },
    { Icon: Music,   bg: ['#3A1A5C','#6A3A9A'], delay: 3.1,  dur: 5.3 },
    { Icon: TreePalm,bg: ['#1A3F28','#3A6A48'], delay: 0.8,  dur: 6.5 },
    { Icon: CupSoda, bg: ['#5C2A1A','#A04A2A'], delay: 2.3,  dur: 5.8 },
    { Icon: Zap,     bg: ['#3A3A1A','#6A6A30'], delay: 4.0,  dur: 6.2 },
  ],
  default: [
    { Icon: Star,    bg: ['#4A3A10','#8A7030'], delay: 0,    dur: 5.8 },
    { Icon: Coffee,  bg: ['#5C3D2E','#8B6347'], delay: 1.5,  dur: 6.2 },
    { Icon: Tag,     bg: ['#1A3A5A','#3A6A9A'], delay: 3.0,  dur: 5.5 },
    { Icon: Ticket,  bg: ['#3A1A5C','#6A3A9A'], delay: 0.8,  dur: 6.0 },
    { Icon: Sparkles,bg: ['#4A3A10','#7A6020'], delay: 2.2,  dur: 5.3 },
    { Icon: Zap,     bg: ['#2A4A2A','#4A7A4A'], delay: 3.8,  dur: 6.5 },
  ],
};

// Absolute positions for floating objects (right side of banner)
const OBJ_POSITIONS = [
  { right: '4%',  top: '12%'    },
  { right: '16%', top: '8%'     },
  { right: '8%',  top: '42%'    },
  { right: '22%', bottom: '22%' },
  { right: '6%',  bottom: '15%' },
  { right: '20%', top: '55%'    },
];

// Floating deal promo badges
const PROMO_BUBBLES = [
  { text: '25% OFF',     Icon: Tag,    delay: 0,   pos: { left: '52%', top: '11%' },    float: 'sp-float-a' },
  { text: 'Free Coffee', Icon: Coffee, delay: 1.3, pos: { left: '58%', bottom: '28%' }, float: 'sp-float-b' },
  { text: 'BOGO Deal',   Icon: Zap,    delay: 2.6, pos: { left: '44%', top: '65%' },    float: 'sp-float-c' },
];

// ─── Countdown Timer ─────────────────────────────────────────────────────────
function Countdown({ endDate }) {
  const [tl, setTl] = useState(null);

  useEffect(() => {
    const calc = () => {
      const diff = new Date(endDate) - Date.now();
      if (diff <= 0) { setTl(null); return; }
      setTl({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
      });
    };
    calc();
    const id = setInterval(calc, 30000);
    return () => clearInterval(id);
  }, [endDate]);

  if (!tl) return null;

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '2px',
      background: 'rgba(12,10,8,0.6)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      border: '1px solid rgba(200,169,126,0.3)',
      borderRadius: '16px',
      padding: '9px 16px',
      marginTop: '16px',
    }}>
      <span style={{ color: 'rgba(246,241,233,0.45)', fontSize: '9px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', marginRight: '8px' }}>Ends in</span>
      {[['d', tl.d], ['h', tl.h], ['m', tl.m]].map(([lbl, val], i) => (
        <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
          {i > 0 && <span style={{ color: 'rgba(200,169,126,0.5)', fontSize: '16px', fontWeight: 300, margin: '0 4px' }}>:</span>}
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#C8A97E', fontSize: '20px', fontWeight: 800, lineHeight: 1, fontFamily: "Georgia,'Times New Roman',serif", letterSpacing: '-0.02em' }}>{String(val).padStart(2,'0')}</div>
            <div style={{ color: 'rgba(246,241,233,0.4)', fontSize: '8px', letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: '2px' }}>{lbl}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Animated Statistics Rotator ─────────────────────────────────────────────
function AnimatedStats({ stats }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx(i => (i + 1) % stats.length), 4000);
    return () => clearInterval(id);
  }, [stats.length]);

  const s = stats[idx];
  return (
    <div
      key={idx}
      style={{
        background: 'rgba(12,10,8,0.55)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        border: '1px solid rgba(200,169,126,0.22)',
        borderRadius: '14px',
        padding: '10px 18px',
        minWidth: '148px',
        textAlign: 'center',
        animation: 'sp-stat-cycle 4s ease-in-out both',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}>
        <s.Icon size={16} style={{ color: '#C8A97E', flexShrink: 0 }} />
        <span style={{ color: '#F6F1E9', fontSize: '20px', fontWeight: 800, fontFamily: "Georgia,'Times New Roman',serif", lineHeight: 1 }}>{s.value}</span>
      </div>
      <div style={{ color: 'rgba(246,241,233,0.45)', fontSize: '9px', letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: '4px' }}>{s.label}</div>
    </div>
  );
}

// ─── Premium CTA Button ───────────────────────────────────────────────────────
function CTAButton({ banner }) {
  const [hov, setHov] = useState(false);
  return (
    <a
      href={banner.cta_link}
      data-testid="spotlight-cta-btn"
      onClick={() => trackEvent(banner.banner_id, 'click')}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '9px',
        padding: '13px 28px',
        background: hov ? 'linear-gradient(135deg,#D4BB88,#C8A97E)' : 'linear-gradient(135deg,#C8A97E,#B89060)',
        color: '#12100e',
        fontSize: '12px',
        fontWeight: 800,
        fontFamily: 'Arial,Helvetica,sans-serif',
        textDecoration: 'none',
        borderRadius: '100px',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        transition: 'all 0.28s cubic-bezier(0.4,0,0.2,1)',
        boxShadow: hov
          ? '0 10px 36px rgba(200,169,126,0.5), 0 2px 8px rgba(0,0,0,0.3)'
          : '0 4px 20px rgba(0,0,0,0.35)',
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
      }}
    >
      {banner.cta_text}
      <ArrowRight
        size={13}
        style={{
          transform: hov ? 'translateX(4px)' : 'translateX(0)',
          transition: 'transform 0.22s ease',
        }}
      />
    </a>
  );
}

// ─── Glass Nav Button ─────────────────────────────────────────────────────────
function GlassNavBtn({ isPrev, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      aria-label={isPrev ? 'Previous spotlight' : 'Next spotlight'}
      data-testid={isPrev ? 'spotlight-prev-btn' : 'spotlight-next-btn'}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: 'absolute',
        [isPrev ? 'left' : 'right']: '18px',
        top: '50%',
        transform: `translateY(-50%) scale(${hov ? 1.12 : 1})`,
        zIndex: 20,
        width: '46px',
        height: '46px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: hov ? 'rgba(200,169,126,0.2)' : 'rgba(246,241,233,0.08)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: `1px solid rgba(200,169,126,${hov ? 0.55 : 0.25})`,
        boxShadow: hov
          ? '0 0 22px rgba(200,169,126,0.35), 0 4px 18px rgba(0,0,0,0.35)'
          : '0 4px 18px rgba(0,0,0,0.28)',
        color: '#F6F1E9',
        cursor: 'pointer',
        transition: 'all 0.24s cubic-bezier(0.4,0,0.2,1)',
      }}
    >
      {isPrev ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
    </button>
  );
}

// ─── BannerSlide — one full cinematic slide ───────────────────────────────────
function BannerSlide({ banner, active, isMultiple, liveStats }) {
  const cardRef    = useRef(null);
  const imageRef   = useRef(null);
  const objRefs    = useRef([]);
  const frameRef   = useRef(null);
  const mousePos   = useRef({ x: 0, y: 0 });
  const hasTracked = useRef(false);

  // Impression tracking
  useEffect(() => {
    if (active && !hasTracked.current && banner?.banner_id) {
      hasTracked.current = true;
      trackEvent(banner.banner_id, 'impression');
    }
  }, [active, banner?.banner_id]);

  // Parallax animation loop
  const handleMouseMove = useCallback((e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mousePos.current = {
      x: ((e.clientX - r.left) / r.width  - 0.5) * 2,
      y: ((e.clientY - r.top)  / r.height - 0.5) * 2,
    };
  }, []);
  const handleMouseLeave = useCallback(() => { mousePos.current = { x: 0, y: 0 }; }, []);

  useEffect(() => {
    if (!active) return;
    let cur = { x: 0, y: 0 };
    const loop = () => {
      cur.x += (mousePos.current.x - cur.x) * 0.06;
      cur.y += (mousePos.current.y - cur.y) * 0.06;
      if (imageRef.current) {
        imageRef.current.style.transform =
          `scale(1.08) translate(${cur.x * -2}px, ${cur.y * -1.5}px)`;
      }
      if (cardRef.current) {
        cardRef.current.style.transform =
          `translate(${cur.x * 12}px, ${cur.y * 8}px)`;
      }
      objRefs.current.forEach((el, i) => {
        if (el) {
          const f = 15 + i * 2;
          el.style.transform = `translate(${cur.x * f}px, ${cur.y * f * 0.65}px)`;
        }
      });
      frameRef.current = requestAnimationFrame(loop);
    };
    frameRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frameRef.current);
  }, [active]);

  const bg       = banner?.image_url || '';
  const mobileBg = banner?.mobile_image_url || bg;
  const theme    = detectTheme(banner);
  const icons    = THEME_ICONS[theme] || THEME_ICONS.default;
  const floatAnims = ['sp-float-a', 'sp-float-b', 'sp-float-c'];

  // Stats items
  const statsItems = liveStats ? [
    { Icon: Flame,   value: liveStats.active_deals, label: 'Active Deals'       },
    { Icon: Coffee,  value: liveStats.businesses,   label: 'Local Businesses'   },
    { Icon: Sparkles,value: liveStats.verified,     label: 'Members Joined'     },
  ] : null;

  const slideTransition = 'opacity 1.3s cubic-bezier(0.25,1,0.5,1), transform 1.4s cubic-bezier(0.25,1,0.5,1)';

  return (
    <div
      className="absolute inset-0 w-full h-full"
      style={{
        opacity: active ? 1 : 0,
        transform: active ? 'scale(1)' : 'scale(1.025)',
        transition: slideTransition,
        pointerEvents: active ? 'auto' : 'none',
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* ── Layer 1: Cinematic Background ── */}
      {banner?.video_url ? (
        <video
          ref={imageRef}
          src={banner.video_url}
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            transform: 'scale(1.08)',
            filter: 'sepia(15%) contrast(115%) saturate(120%) brightness(80%)',
          }}
        />
      ) : bg ? (
        <>
          <img
            ref={imageRef}
            src={bg}
            alt={banner?.title || 'Spotlight'}
            loading="eager"
            className="absolute inset-0 w-full h-full object-cover hidden sm:block"
            style={{
              transformOrigin: 'center center',
              transform: 'scale(1.0)',
              animation: active ? 'sp-kenburns 22s ease-in-out infinite alternate' : 'none',
              filter: 'sepia(15%) contrast(115%) saturate(120%) brightness(80%)',
              transition: 'opacity 0.3s ease',
            }}
          />
          <img
            src={mobileBg}
            alt={banner?.title || 'Spotlight'}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover sm:hidden"
            style={{ filter: 'sepia(12%) contrast(112%) saturate(115%) brightness(78%)' }}
          />
        </>
      ) : (
        <div className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg,#12100e 0%,#1e1912 50%,#1a1510 100%)' }} />
      )}

      {/* ── Overlays: vignette + directional gradients ── */}
      {/* Radial vignette for depth */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 90% 90% at 50% 50%, transparent 30%, rgba(0,0,0,0.75) 100%)',
      }} />
      {/* Left reading zone */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'linear-gradient(to right, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.42) 45%, rgba(0,0,0,0.1) 100%)',
      }} />
      {/* Bottom lift */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 40%)',
      }} />
      {/* Warm tonal wash */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'linear-gradient(135deg,rgba(18,12,6,0.3) 0%,transparent 55%)',
      }} />

      {/* ── Layer 4: Diagonal Light Sweep ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 2 }}>
        <div style={{
          position: 'absolute',
          top: '-25%',
          left: 0,
          width: '30%',
          height: '150%',
          background: 'linear-gradient(to right, transparent 0%, rgba(200,169,126,0.07) 35%, rgba(220,185,140,0.14) 50%, rgba(200,169,126,0.07) 65%, transparent 100%)',
          animation: 'sp-lightsweep 9s ease-in-out 2s infinite',
        }} />
      </div>

      {/* ── Layer 3: Floating Premium Icons ── */}
      <div className="absolute inset-0 pointer-events-none hidden sm:block" style={{ zIndex: 3 }}>
        {icons.map(({ Icon, bg: colors, delay, dur }, i) => (
          <div
            key={i}
            ref={el => { objRefs.current[i] = el; }}
            style={{
              position: 'absolute',
              ...OBJ_POSITIONS[i],
              width: '58px',
              height: '58px',
              borderRadius: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`,
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: `0 10px 36px rgba(0,0,0,0.4), 0 2px 0 rgba(255,255,255,0.08) inset`,
              animation: `${floatAnims[i % 3]} ${dur}s ease-in-out ${delay}s infinite`,
              filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.45))',
            }}
          >
            <Icon size={26} style={{ color: 'rgba(246,241,233,0.9)', strokeWidth: 1.5 }} />
          </div>
        ))}
      </div>

      {/* ── Trending Badge — top left ── */}
      <div style={{
        position: 'absolute',
        top: '22px',
        left: '22px',
        zIndex: 12,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        padding: '6px 14px',
        background: 'rgba(12,10,8,0.62)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        border: '1px solid rgba(200,169,126,0.3)',
        borderRadius: '100px',
        pointerEvents: 'none',
      }}>
        <span style={{
          width: '7px', height: '7px', borderRadius: '50%',
          background: '#C8A97E',
          animation: 'sp-pulse-dot 2.2s ease-in-out infinite',
          display: 'block',
          flexShrink: 0,
        }} />
        <span style={{ color: '#C8A97E', fontSize: '9px', fontWeight: 800, letterSpacing: '0.2em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          Trending This Week
        </span>
      </div>

      {/* ── Sponsor Chip — top right ── */}
      {banner?.sponsor_name && (
        <div style={{
          position: 'absolute',
          top: '18px',
          right: '18px',
          zIndex: 12,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'rgba(246,241,233,0.07)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(200,169,126,0.38)',
          borderRadius: '100px',
          padding: '6px 14px 6px 8px',
        }}>
          {banner.sponsor_logo_url ? (
            <img
              src={banner.sponsor_logo_url}
              alt={banner.sponsor_name}
              style={{ height: '20px', width: 'auto', objectFit: 'contain', borderRadius: '4px', flexShrink: 0, filter: 'brightness(1.1)' }}
            />
          ) : (
            <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'rgba(200,169,126,0.22)', flexShrink: 0 }} />
          )}
          <div>
            <p style={{ color: 'rgba(246,241,233,0.42)', fontSize: '8px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', margin: 0, lineHeight: 1.2 }}>Presented by</p>
            <p style={{ color: '#C8A97E', fontSize: '11px', fontWeight: 700, margin: 0, lineHeight: 1.3, letterSpacing: '0.02em' }}>{banner.sponsor_name}</p>
          </div>
        </div>
      )}

      {/* ── Floating Deal Promo Bubbles ── */}
      <div className="absolute inset-0 pointer-events-none hidden sm:block" style={{ zIndex: 4 }}>
        {PROMO_BUBBLES.map(({ text, Icon, delay, pos, float: anim }, i) => (
          <div
            key={i}
            data-testid={`spotlight-promo-bubble-${i}`}
            style={{
              position: 'absolute',
              ...pos,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 13px',
              background: 'rgba(246,241,233,0.08)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(200,169,126,0.28)',
              borderRadius: '100px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
              animation: `${anim} ${3.8 + i * 0.6}s ease-in-out ${delay}s infinite`,
            }}
          >
            <Icon size={12} style={{ color: '#C8A97E', flexShrink: 0 }} />
            <span style={{ color: '#F6F1E9', fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{text}</span>
          </div>
        ))}
      </div>

      {/* ── Layer 2: Luxury Glass Card ── */}
      <div className="absolute inset-0 flex items-center" style={{
        paddingLeft: 'clamp(18px,4.5vw,72px)',
        paddingRight: 'clamp(18px,4.5vw,72px)',
        zIndex: 8,
      }}>
        <div
          ref={cardRef}
          className="will-change-transform"
          style={{
            maxWidth: '460px',
            width: '100%',
            background: 'rgba(28,24,18,0.42)',
            backdropFilter: 'blur(22px)',
            WebkitBackdropFilter: 'blur(22px)',
            border: '1px solid rgba(200,169,126,0.32)',
            borderRadius: '28px',
            boxShadow: '0 30px 60px -15px rgba(0,0,0,0.6), 0 2px 0 rgba(200,169,126,0.2) inset, 0 -1px 0 rgba(0,0,0,0.3) inset',
            padding: 'clamp(22px,3vw,38px)',
            position: 'relative',
            overflow: 'hidden',
            animation: active ? 'sp-card-enter 0.9s cubic-bezier(0.25,1,0.5,1) both' : 'none',
          }}
        >
          {/* Inner top highlight line */}
          <div style={{
            position: 'absolute', top: 0, left: '15%', right: '15%', height: '1px',
            background: 'linear-gradient(to right, transparent, rgba(200,169,126,0.55), transparent)',
          }} />
          {/* Inner bottom reflection */}
          <div style={{
            position: 'absolute', bottom: 0, left: '25%', right: '25%', height: '1px',
            background: 'linear-gradient(to right, transparent, rgba(200,169,126,0.18), transparent)',
          }} />
          {/* Light sweep across card */}
          <div style={{
            position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: '28px', pointerEvents: 'none',
          }}>
            <div style={{
              position: 'absolute', top: '-20%', left: '-10%', width: '40%', height: '140%',
              background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.04), transparent)',
              animation: 'sp-lightsweep 11s ease-in-out 5s infinite',
            }} />
          </div>

          {/* Animated gold accent line */}
          <div style={{
            height: '2px',
            background: 'linear-gradient(to right,#C8A97E,#E8C98A,#C8A97E)',
            backgroundSize: '200% auto',
            animation: 'sp-accent-breathe 4s ease-in-out infinite, sp-shimmer 6s linear infinite',
            borderRadius: '2px',
            marginBottom: '18px',
            boxShadow: '0 0 10px rgba(200,169,126,0.45)',
          }} />

          {/* Title */}
          <h2 style={{
            margin: '0 0 10px',
            color: '#F6F1E9',
            fontSize: 'clamp(20px,3.4vw,40px)',
            lineHeight: 1.1,
            fontFamily: "Georgia,'Times New Roman',serif",
            fontWeight: 700,
            letterSpacing: '-0.025em',
            textShadow: '0 2px 16px rgba(0,0,0,0.5)',
          }}>
            {banner?.title || 'Athens Spotlight'}
          </h2>

          {/* Subtitle */}
          {banner?.subtitle && (
            <p style={{
              margin: '0 0 20px',
              color: 'rgba(246,241,233,0.70)',
              fontSize: '14px',
              lineHeight: 1.65,
              fontFamily: 'Arial,Helvetica,sans-serif',
              textShadow: '0 1px 6px rgba(0,0,0,0.4)',
            }}>
              {banner.subtitle}
            </p>
          )}

          {/* Countdown badge */}
          {banner?.end_date && <Countdown endDate={banner.end_date} />}

          {/* CTA */}
          {banner?.cta_text && banner?.cta_link && (
            <div style={{ marginTop: '20px' }}>
              <CTAButton banner={banner} />
            </div>
          )}
        </div>
      </div>

      {/* ── Animated Stats — bottom right ── */}
      {statsItems && (
        <div
          className="absolute hidden sm:block"
          style={{ bottom: '24px', right: '22px', zIndex: 10 }}
        >
          <AnimatedStats stats={statsItems} />
        </div>
      )}

      {/* ── Scroll Indicator — bottom center (single banner only) ── */}
      {!isMultiple && (
        <div className="absolute hidden sm:flex" style={{
          bottom: '22px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
          pointerEvents: 'none',
        }}>
          <span style={{ color: 'rgba(246,241,233,0.38)', fontSize: '9px', letterSpacing: '0.2em', textTransform: 'uppercase' }}>Discover More</span>
          <div style={{ animation: 'sp-bounce-arrow 1.9s ease-in-out infinite' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 3.5v11M5 10.5l4 4 4-4" stroke="rgba(200,169,126,0.65)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main SpotlightBanner component ──────────────────────────────────────────
export default function SpotlightBanner() {
  const [data, setData]         = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loaded, setLoaded]     = useState(false);
  const [liveStats, setLiveStats] = useState(null);
  const intervalRef             = useRef(null);

  useEffect(() => { injectKeyframes(); }, []);

  useEffect(() => {
    fetch(`${API}/spotlight/active`)
      .then(r => r.json())
      .then(d => { setData(d); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    fetch(`${API}/stats/live`)
      .then(r => r.json())
      .then(setLiveStats)
      .catch(() => {});
  }, []);

  const banners  = data?.banners || [];
  const config   = data?.config  || {};
  const enabled  = data?.enabled && banners.length > 0;
  const interval = (config.rotation_interval || 5) * 1000;

  const goNext = useCallback(() => setCurrentIdx(i => (i + 1) % banners.length), [banners.length]);
  const goPrev = useCallback(() => setCurrentIdx(i => (i - 1 + banners.length) % banners.length), [banners.length]);

  useEffect(() => {
    if (!enabled || banners.length <= 1 || config.rotation_mode !== 'auto') return;
    intervalRef.current = setInterval(goNext, interval);
    return () => clearInterval(intervalRef.current);
  }, [enabled, banners.length, config.rotation_mode, interval, goNext]);

  if (!loaded || !enabled) return null;

  return (
    <div
      className="w-full px-1 sm:px-3 py-6 sm:py-8"
      style={{ background: 'transparent' }}
    >
      <section
        data-testid="spotlight-banner"
        className="relative w-full overflow-hidden mx-auto"
        style={{
          height: 'clamp(340px, 58vw, 680px)',
          background: '#12100e',
          borderRadius: '28px',
          boxShadow: '0 24px 80px rgba(0,0,0,0.28), 0 4px 24px rgba(0,0,0,0.18)',
          maxWidth: '1640px',
        }}
        aria-label="Perkly Spotlight"
      >
        {banners.map((banner, i) => (
          <BannerSlide
            key={banner.banner_id}
            banner={banner}
            active={i === currentIdx}
            isMultiple={banners.length > 1}
            liveStats={liveStats}
          />
        ))}

        {/* Glass Navigation */}
        {banners.length > 1 && (
          <>
            <GlassNavBtn isPrev onClick={goPrev} />
            <GlassNavBtn isPrev={false} onClick={goNext} />

            {/* Dot indicators */}
            <div style={{
              position: 'absolute',
              bottom: '22px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '7px',
              zIndex: 20,
            }}>
              {banners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIdx(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  style={{
                    width: i === currentIdx ? '26px' : '7px',
                    height: '7px',
                    borderRadius: '4px',
                    background: i === currentIdx ? '#C8A97E' : 'rgba(246,241,233,0.38)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'width 0.35s ease, background 0.35s ease',
                    padding: 0,
                    boxShadow: i === currentIdx ? '0 0 10px rgba(200,169,126,0.55)' : 'none',
                  }}
                />
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
